import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllCourses } from '../services/courseService';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState(null);
  
  // Mock recent activities for demo
  const recentActivities = [
    {
      id: 1,
      type: 'lesson_completed',
      courseTitle: 'AI Fundamentals',
      lessonTitle: 'Supervised Learning',
      date: '2 days ago'
    },
    {
      id: 2,
      type: 'quiz_passed',
      courseTitle: 'AI Fundamentals',
      quizTitle: 'Machine Learning Basics',
      score: 85,
      date: '1 week ago'
    },
    {
      id: 3,
      type: 'course_started',
      courseTitle: 'AI Fundamentals',
      date: '2 weeks ago'
    }
  ];
  
  // Get first name from email for personalized greeting
  const firstName = currentUser?.email?.split('@')[0] || 'Student';
  const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  // Load courses on component mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        const allCourses = await getAllCourses();
        setCourses(allCourses);
        
        // Set the first course as active for demo purposes
        if (allCourses.length > 0) {
          const courseDetails = await fetch(`/lessons/ai-fundamentals/introduction.nles`).then(res => {
            if (!res.ok) return null;
            return res.text();
          });
          
          if (courseDetails) {
            setActiveCourse({
              ...allCourses[0],
              progress: 40,
              completedLessons: 4,
              totalLessons: 10,
              lastLesson: 'Supervised Learning',
            });
          } else {
            setActiveCourse({
              ...allCourses[0],
              progress: 40,
              completedLessons: 4,
              totalLessons: 10,
              lastLesson: 'Supervised Learning',
            });
          }
        }
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCourses();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Dashboard Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-gray-900 dark:text-white">
          Welcome back, {capitalizedFirstName}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Continue your AI learning journey
        </p>
      </div>

      {/* Dashboard Stats */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-500 dark:text-gray-400">Course Progress</h3>
            <span className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">{activeCourse?.progress || 0}%</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Of {activeCourse?.title || 'your current course'} completed
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-500 dark:text-gray-400">Lessons Completed</h3>
            <span className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeCourse?.completedLessons || 0} / {activeCourse?.totalLessons || 0}
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Lessons finished so far
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-500 dark:text-gray-400">Learning Streak</h3>
            <span className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
              </svg>
            </span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">5 Days</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Keep up the momentum!
          </p>
        </div>
      </div> */}

      {/* Continue Learning Section */}
      {/* <div className="mb-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">
            Continue Learning
          </h2>
          <Link to="/dashboard/courses" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
            View all courses
          </Link>
        </div>

        {activeCourse ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="h-52 overflow-hidden relative">
              <img 
                src={activeCourse.image} 
                alt={activeCourse.title} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="px-2.5 py-0.5 bg-primary-500 bg-opacity-90 text-white text-xs font-medium rounded">
                      {activeCourse.category}
                    </span>
                    <span className="px-2.5 py-0.5 bg-gray-700 bg-opacity-90 text-white text-xs font-medium rounded">
                      {activeCourse.level}
                    </span>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-1">
                    {activeCourse.title}
                  </h3>
                  <p className="text-sm text-gray-300">
                    {activeCourse.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Your progress
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {activeCourse.completedLessons}/{activeCourse.totalLessons} lessons completed ({activeCourse.progress}%)
                </span>
              </div>

              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
                <div 
                  className="bg-primary-600 dark:bg-primary-500 h-2.5 rounded-full" 
                  style={{ width: `${activeCourse.progress}%` }}
                ></div>
              </div>

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Last completed:</span> {activeCourse.lastLesson}
                </div>
                <Link 
                  to={`/dashboard/courses/${activeCourse.id}`}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
                >
                  Continue Course
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              No courses in progress. Start learning by exploring our courses!
            </p>
            <Link 
              to="/dashboard/courses" 
              className="mt-4 inline-block px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
            >
              Explore Courses
            </Link>
          </div>
        )}
      </div> */}

      {/* Your Courses Section */}
      {courses.length > 0 && (
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">
              Your Courses
            </h2>
            <Link to="/dashboard/courses" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
              View all
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.slice(0, 3).map(course => (
              <Link 
                to={`/dashboard/courses/${course.id}`} 
                key={course.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="relative pb-[60%]">
                  <img 
                    src={course.image} 
                    alt={course.title} 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      course.level === 'Beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                      course.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                    }`}>
                      {course.level}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold text-lg text-gray-900 dark:text-white mb-1">{course.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-2">{course.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity Section */}
      {/* <div>
        <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-6">
          Recent Activity
        </h2>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          {recentActivities.length > 0 ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivities.map(activity => (
                <li key={activity.id} className="p-4">
                  <div className="flex items-start">
                    <div className="mr-4 mt-1">
                      {activity.type === 'lesson_completed' && (
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                      )}
                      {activity.type === 'quiz_passed' && (
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </span>
                      )}
                      {activity.type === 'course_started' && (
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 dark:text-white font-medium">
                        {activity.type === 'lesson_completed' && `Completed "${activity.lessonTitle}" lesson`}
                        {activity.type === 'quiz_passed' && `Passed "${activity.quizTitle}" quiz with ${activity.score}%`}
                        {activity.type === 'course_started' && `Started "${activity.courseTitle}" course`}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="text-primary-600 dark:text-primary-400">{activity.courseTitle}</span> • {activity.date}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-600 dark:text-gray-400">No recent activity to display.</p>
            </div>
          )}
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard; 